// External packages
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

// Types
import type { Place } from '../../../server/src/types/place.types';

// Schemas
import { placeCreateSchema } from 'schemas/place';

// Hooks
import { useToast } from 'hooks/useToast';
import { API_BASE_URL, authHeaders } from './base';

export interface PlaceResponse {
  error: boolean;
  data: Place[];
}

export async function getPlaces(): Promise<Place[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/places`, {
      headers: authHeaders(),
    });

    if (!response.ok) throw new Error('Failed to fetch places');

    const result: PlaceResponse = await response.json();

    if (result.error) throw new Error('Failed to fetch places');

    return result.data;
  } catch (error: any) {
    throw new Error(error.message || 'Network error during fetching places');
  }
}

export const useGetPlaces = () => {
  return useQuery({
    queryKey: ['places'],
    queryFn: getPlaces,
    initialData: [],
  });
};

export async function getPlaceById(id: string): Promise<Place> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/places/${id}`,
      { headers: authHeaders() }
    );

    if (!response.ok) throw new Error('Failed to fetch place');

    const result = await response.json();

    if (result.error) throw new Error('Failed to fetch place');

    return result.data;
  } catch (error: any) {
    throw new Error(error.message || 'Network error during fetching place');
  }
}

export const useGetPlaceById = (id: string) => {
  return useQuery({
    queryKey: ['places', id],
    queryFn: () => getPlaceById(id),
    enabled: !!id,
  });
};

export const createPlace = async (
  data: z.infer<typeof placeCreateSchema>,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/places`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok)
      throw new Error(`Failed to create place: ${response.statusText}`);

    return response.json();
  } catch (error: any) {
    throw new Error(error.message || 'Network error during place creation');
  }
};

export const useCreatePlace = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ data }: { data: z.infer<typeof placeCreateSchema> }) =>
      createPlace(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] });
      toast.success('Soba je uspješno kreirana!');
    },
    onError: (error: any) => {
      console.error('Failed to create place:', error);
      toast.error(error?.message || 'Greška pri kreiranju sobe.');
    },
  });
};

export const updatePlace = async (
  id: string,
  data: z.infer<typeof placeCreateSchema>,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/places/${id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok)
      throw new Error(`Failed to update place: ${response.statusText}`);

    return response.json();
  } catch (error: any) {
    throw new Error(error.message || 'Network error during place update');
  }
};

export const useUpdatePlace = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: z.infer<typeof placeCreateSchema>;
    }) => updatePlace(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] });
      toast.success('Soba je uspješno ažurirana!');
    },
    onError: (error: any) => {
      console.error('Failed to update place:', error);
      toast.error(error?.message || 'Greška pri ažuriranju sobe.');
    },
  });
};

export const reorderPlaceExhibits = async (
  placeId: string,
  exhibitIds: string[],
  authToken: string = localStorage.getItem('authToken') || ''
): Promise<void> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/places/${placeId}/exhibits/order`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ exhibitIds }),
      }
    );

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message || 'Failed to reorder exhibits');
    }
  } catch (error: any) {
    throw new Error(error.message || 'Network error during exhibit reorder');
  }
};

export const useReorderPlaceExhibits = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({
      placeId,
      exhibitIds,
    }: {
      placeId: string;
      exhibitIds: string[];
    }) => reorderPlaceExhibits(placeId, exhibitIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] });
      toast.success('Redoslijed eksponata je spremljen!');
    },
    onError: (error: any) => {
      console.error('Failed to reorder exhibits:', error);
      toast.error(error?.message || 'Greška pri spremanju redoslijeda.');
    },
  });
};

export const deletePlace = async (id: string): Promise<void> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/places/${id}`,
      { method: 'DELETE', headers: authHeaders() }
    );

    if (!response.ok) throw new Error('Failed to delete place');
  } catch (error: any) {
    throw new Error(error.message || 'Network error during place deletion');
  }
};

export const useDeletePlace = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: deletePlace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] });
      toast.success('Soba je uspješno obrisana!');
    },
    onError: (error: any) => {
      console.error('Failed to delete place:', error);
      toast.error(error?.message || 'Greška pri brisanju sobe.');
    },
  });
};
